import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { CustomerProfile } from './models/customer-profile.model';
import { GenericResponse } from '../shared/model/generic-response.model';


/*
 * Security Service
 *
 * TECHNOLOGY: Angular Service (Injectable)
 * This service acts as the central hub for all authentication-related API calls.
 * It uses Angular's HttpClient to communicate with the Java Spring Boot backend.
 *
 * RESPONSIBILITIES:
 * 1. Managing Auth state (login/logout).
 * 2. Communicating with REST endpoints (register, reset password, etc.).
 * 3. Providing helper methods for other components (getUserByLoginId).
 */
@Injectable({
  providedIn: 'root'
})
export class SecurityService {

  //secretAuthUser = {'userName': 'user1','password': 'Spring10'}
  public apiURL = environment.apiUrl;
  private caid = environment.caid;
  private SecurityServiceURL = this.apiURL + "/api/ksolve-auth/authenticate";
  private registerServiceURL = this.apiURL + "/register-customer";
  private emailVerificationServiceURL = this.apiURL + "/validate-email-verification-code";
  private resendEmailVerificationCodeServiceURL = this.apiURL + "/resend-email-verification-code";
  private resetPasswordServiceURL = this.apiURL + "/reset-password";

  private userServiceURL = this.apiURL + "/user/byloginid/";
  private customerProfileServiceURL = this.apiURL + "/customer-profile/by-loginid-and-caid/";
  private countryServiceURL = this.apiURL + "/lookup/countries";
  private countryPhoneCodesServiceURL = this.apiURL + "/lookup/country-phone-codes";
  private stateServiceURL = this.apiURL + "/lookup/states/";
  private saveUserPhonesServiceURL = this.apiURL + "/user/save-phones";
  private saveUserHomeOrOfficeAddressServiceURL = this.apiURL + "/user/save-home-or-office-address";
  private saveUserMailingOrShippingAddressServiceURL = this.apiURL + "/user/save-mailing-or-shipping-address";

  //private createStripePaymentInteServiceURL = "http://localhost:8090/create-payment-intent";
  private createStripePaymentInteServiceURL = this.apiURL + "/create-payment-intent";

  private isLoggedInSubject = new BehaviorSubject<boolean>(this.loggedIn());
  isLoggedIn$ = this.isLoggedInSubject.asObservable();

  private isMwmLoggedInSubject = new BehaviorSubject<boolean>(this.mwmLoggedIn());
  isMwmLoggedIn$ = this.isMwmLoggedInSubject.asObservable();

  constructor(private http: HttpClient) { }

  registerUser(user: CustomerProfile) {
    //const headers = new HttpHeaders().set('Authorization',authToken);
    console.log("SecurityService registerUser CAID:", this.caid);
    user.caid = this.caid;
    return this.http.post<GenericResponse>(this.registerServiceURL, user);
  }

  verifyEmail(user: CustomerProfile) {
    user.caid = this.caid;
    return this.http.post<any>(this.emailVerificationServiceURL, user);
  }

  resendEmailVerificationCode(user: CustomerProfile) {
    user.caid = this.caid;
    return this.http.post<any>(this.resendEmailVerificationCodeServiceURL, user);
  }

  resetPassword(user: CustomerProfile) {
    user.caid = this.caid;
    return this.http.post<GenericResponse>(this.resetPasswordServiceURL, user);
  }

  mwmForgotPassword(email: string) {
    return this.http.post<any>(this.apiURL + "/api/mwm-auth/forgot-password", { email });
  }

  mwmResetPassword(data: any) {
    return this.http.post<any>(this.apiURL + "/api/mwm-auth/reset-password", data);
  }

  mwmVerifyEmail(data: any) {
    return this.http.post<any>(this.apiURL + "/api/mwm-auth/verify-email", data);
  }

  updateMwmProfile(data: any) {
    return this.http.post<any>(this.apiURL + "/api/mwm-auth/update-profile", data);
  }

  registerMwmParent(data: any) {
    return this.http.post<any>(this.apiURL + "/api/mwm-auth/register", data);
  }

  mwmLogin(data: any) {
    return this.http.post<any>(this.apiURL + "/api/mwm-auth/login", data);
  }

  mwmSetSession(token: string, email: string, username: string) {
    localStorage.setItem('MwmAuthToken', token);
    localStorage.setItem('MwmUser', JSON.stringify({ email, username }));
    this.updateLoginStatus();
  }

  getMwmUser(): any {
    const userStr = localStorage.getItem('MwmUser');
    return userStr ? JSON.parse(userStr) : null;
  }

  getMwmUrl(endpoint: string): string {
    return this.apiURL + endpoint;
  }

  mwmGetProfile(email: string) {
    return this.http.get<any>(this.apiURL + "/api/mwm-auth/profile/" + email);
  }

  mwmUpdateParentAdmin(id: number, data: any) {
    const token = this.getMwmAuthToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.put<any>(this.apiURL + "/api/mwm-admin/parents/" + id, data, { headers });
  }

  getCountries() {
    return this.http.get<any[]>(this.apiURL + "/lookup/countries");
  }

  getStates(countryId: number) {
    return this.http.get<any[]>(this.apiURL + "/lookup/states/" + countryId);
  }

  getCities(stateId: number) {
    return this.http.get<any[]>(this.apiURL + "/lookup/cities/" + stateId);
  }

  mwmCreateClass(data: any) {
    const token = this.getMwmAuthToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.post<any>(this.apiURL + "/api/mwm-admin/classes", data, { headers });
  }

  mwmUpdateClass(id: number, data: any) {
    const token = this.getMwmAuthToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.put<any>(this.apiURL + "/api/mwm-admin/classes/" + id, data, { headers });
  }

  mwmDeleteClass(id: number) {
    const token = this.getMwmAuthToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.delete<any>(this.apiURL + "/api/mwm-admin/classes/" + id, { headers });
  }

  //Authenticates user and returns jwt auth token
  /*
   * TECHNOLOGY: HTTP POST Request
   * Sends user credentials to the backend. The backend response is expected to 
   * contain a JWT (JSON Web Token) if authentication is successful.
   * This specific call is handled by the component to store the token.
   */
  authenticateUser(user) {
    console.log("SecurityService authenticateUser CAID:", this.caid);
    user.caid = this.caid;
    return this.http.post<any>(this.SecurityServiceURL, user);
  }

  getUserByLoginId(loginId) {
    const headers = new HttpHeaders().set('Authorization', this.getAuthToken());
    return this.http.get<any>(this.userServiceURL + loginId, { headers });

  }

  getCustomerProfileByLoginId(loginId) {
    const headers = new HttpHeaders().set('Authorization', this.getAuthToken());
    console.log("Security Service: Login Id : " + loginId);
    return this.http.get<CustomerProfile>(this.customerProfileServiceURL + loginId + "/" + this.caid, { headers });
  }

  loggedIn() {
    return !!localStorage.getItem('AuthToken');
  }

  mwmLoggedIn() {
    return !!localStorage.getItem('MwmAuthToken');
  }

  getAuthToken() {
    return localStorage.getItem('AuthToken');
  }

  getMwmAuthToken() {
    return localStorage.getItem('MwmAuthToken');
  }

  getCaid() {
    return this.caid;
  }

  getCountryPhoneCodes() {
    const headers = new HttpHeaders().set('Authorization', this.getAuthToken());
    return this.http.get<any>(this.countryPhoneCodesServiceURL, { headers });
  }

  saveUserPhones(user: CustomerProfile) {
    const headers = new HttpHeaders().set('Authorization', this.getAuthToken());
    user.caid = this.caid;
    return this.http.post<any>(this.saveUserPhonesServiceURL, (user), { headers });
  }

  saveUserHomeOrOfficeAddress(user: CustomerProfile) {
    const headers = new HttpHeaders().set('Authorization', this.getAuthToken());
    user.caid = this.caid;
    return this.http.post<any>(this.saveUserHomeOrOfficeAddressServiceURL, (user), { headers });
  }

  saveUserMailingOrShippingAddress(user: CustomerProfile) {
    const headers = new HttpHeaders().set('Authorization', this.getAuthToken());
    user.caid = this.caid;
    return this.http.post<any>(this.saveUserMailingOrShippingAddressServiceURL, (user), { headers });
  }

  createStripePaymentIntent(paymentData) {
    const headers = new HttpHeaders().set('Authorization', this.getAuthToken());
    return this.http.post<any>(this.createStripePaymentInteServiceURL, (paymentData), { headers });
  }

  logout() {
    localStorage.removeItem('AuthToken');
    localStorage.removeItem('User');
    localStorage.removeItem('AuthUser');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('user');
    localStorage.removeItem('MwmAuthToken');
    localStorage.removeItem('MwmUser');
    this.updateLoginStatus();
  }

  updateLoginStatus() {
    this.isLoggedInSubject.next(this.loggedIn());
    this.isMwmLoggedInSubject.next(this.mwmLoggedIn());
  }

}
